'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import Analytics from '@utils/Analytics';
import {
    calculateRequiredScaling,
    calculateTotalYield,
    getBaseServings
} from '@utils/servingsCalculations';
import { calculateYieldWarnings } from '@utils/YieldWarnings';
import DishImage from './DishImage.react';
import WarningsModal from '@components/Planner/Modals/WarningsModal.react';
import '@pro/components/Modals/Modals.scss';
import './EditMealServingsModalPrimary.scss';
import './EditMealServingsModalSecondary.scss';

export default class EditMealServingsModalSecondary extends Component {
    static propTypes = {
        meal: PropTypes.object,
        contents: PropTypes.object,
        scaling: PropTypes.number,
        mealDishes: PropTypes.array,
        onChangeScaling: PropTypes.func,
        closeModal: PropTypes.func,
    };

    static defaultProps = {
    };

    constructor(props) {
        super(props);

        const { mealDishes, scaling, contents } = props;

        const dishes= [];
        if(mealDishes) {
            mealDishes.forEach(meal => {
                const dish = { ...meal, main: meal };
                dishes.push(dish);
            });
        }

        // Calculate initial requestedServings based on current scaling
        const initialScaling = scaling || 1;
        const baseServings = dishes.reduce((total, dish) => {
            const content = contents[dish.main.recipe_uuid || dish.main.food_uuid];
            const servings = getBaseServings(content);
            return total + (servings || 0);
        }, 0);
        const initialRequestedServings = baseServings * initialScaling;

        this.state = {
            dishes,
            scaling: initialScaling,
            dirty: false,
            showWarningsModal: false,
            requestedServings: initialRequestedServings,
        };
    }

    componentDidMount =  () => {
        const { meal } = this.props;
        Analytics.editMeal({"Meal UUID": meal ? meal.uuid : null});
    }

    onViewYieldNotes = () => {
        this.setState({showWarningsModal: true});
    }

    closeWarningsModal = () => {
        this.setState({showWarningsModal: false});
    }

    getBaseServingsTotal = (dishes, contents) => {
        if (!dishes || !dishes.length || !contents) return 0;

        return dishes.reduce((total, dish) => {
            const content = contents[dish.main.recipe_uuid || dish.main.food_uuid];
            const baseServings = getBaseServings(content);
            return total + (baseServings || 0);
        }, 0);
    }

    getServingOptions = () => {
        const { dishes } = this.state;
        const { contents } = this.props;
        const baseServings = this.getBaseServingsTotal(dishes, contents);

        return [
            { value: baseServings, label: `${baseServings} servings` },
            { value: baseServings * 2, label: `${baseServings * 2} servings` },
            { value: baseServings * 3, label: `${baseServings * 3} servings` }
        ];
    }

    onChangeServingsNeeded = (servingsNeeded) => {
        const { dishes } = this.state;
        const { contents } = this.props;
        const baseServings = this.getBaseServingsTotal(dishes, contents);

        // Calculate scaling using Math.ceil to ensure whole batches
        const newScaling = calculateRequiredScaling(servingsNeeded, baseServings);

        this.setState({
            scaling: newScaling,
            requestedServings: servingsNeeded,
            dirty: true
        }, () => {
            this.recalculateScaling();
        });
    }

    getTotalServings = () => {
        const { dishes, scaling } = this.state;
        const { contents } = this.props;

        return dishes.map(({main: dish}) => {
            const content = contents[dish.recipe_uuid || dish.food_uuid];
            const baseServings = getBaseServings(content);
            const totalYield = calculateTotalYield(scaling, baseServings);

            return {
                uuid: dish.uuid || dish.recipe_uuid || dish.food_uuid,
                title: content?.title || content?.name,
                totalYield,
                totalServings: totalYield,
                scaling
            };
        });
    }

    recalculateScaling = () => {
        const dishServings = this.getTotalServings();
        const { dishes } = this.state;

        const updatedDishes = dishes.map(dish => {
            const dishServing = dishServings.find(d => d.uuid === (dish.uuid || dish.main.recipe_uuid || dish.main.food_uuid));

            return {
                ...dish,
                scaling: dishServing ? dishServing.scaling : dish.scaling
            };
        });

        this.setState({ dishes: updatedDishes });
    }

    onChangeScaling = () => {
        const { scaling, dirty } = this.state;
        const { onChangeScaling, closeModal, meal } = this.props;

        // Analytics
        if (dirty) {
            Analytics.saveMeal({
                "Edited Servings": true,
                "Meal UUID": meal ? meal.uuid : null
            });
        }

        // Call parent with scaling only
        if (onChangeScaling) {
            onChangeScaling(scaling, dirty);
        }

        closeModal();
    }

    render() {
        const { contents, closeModal } = this.props;
        const { dishes, scaling } = this.state;

        const dishServings = this.getTotalServings();
        const servingOptions = this.getServingOptions();
        const baseServings = this.getBaseServingsTotal(dishes, contents);
        const totalServings = calculateTotalYield(scaling, baseServings);

        // Calculate warnings using synthetic participant with user's requested servings
        // Note: Use uuid other than 'USER' to avoid getLoggedServingsOfRecipe logic
        const { requestedServings } = this.state;
        const syntheticParticipant = { uuid: 'SCALING_MODE', portion: requestedServings || totalServings };
        const warnings = calculateYieldWarnings(dishes, contents, [syntheticParticipant], 0);
        const hasWarnings = warnings.length > 0;

        return (
            <Modal isOpen={true}
                onRequestClose={closeModal}
                closeModal={closeModal}
                className="el-modal el-modal2"
                overlayClassName="el-modal-overlay"
                contentLabel="Edit Meal Servings"
                closeTimeoutMS={250}>

                <div className="el-modal-container el-modal2-container edit-meal-servings-v2-container">
                    <header className="modal-header">
                        <h2>Edit Meal Servings</h2>
                        <button onClick={closeModal} className="el-modal-close-x">
                            <i className="icon-close-x" />
                            <span className="assistive-text">Close Modal</span>
                        </button>
                    </header>

                    <div className="el-modal-body-container el-modal2-body-container">
                        {/* In this Meal Section */}
                        <div className="in-this-meal-section">
                            <div className="section-header">
                                <h3>In this Meal</h3>
                                {hasWarnings && (
                                    <button className="view-yield-notes" onClick={this.onViewYieldNotes}>View yield notes</button>
                                )}
                            </div>
                            <div className="meal-items">
                                {dishServings.map(({uuid, title, totalYield}, index) => {
                                    // Find the corresponding dish and content to get image
                                    const dish = dishes.find(d => d.uuid === uuid);
                                    const content = dish ? contents[dish.main.recipe_uuid || dish.main.food_uuid] : null;
                                    const imageUrl = content?.image;
                                    
                                    return (
                                        <div key={index} className="meal-item">
                                            <DishImage imageUrl={imageUrl} title={title} />
                                            <div className="meal-item-details">
                                                <div className="meal-item-name">
                                                    {title}
                                                </div>
                                                <div className="meal-item-yield">Yield: {totalYield} servings</div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* How many servings do you need Section */}
                        <div className="servings-needed-section">
                            <div className="section-header">
                                <h3>How many servings do you need?</h3>
                            </div>
                            <div className="servings-controls">
                                <div className="count-controls">
                                    <button
                                        className="count-btn minus"
                                        onClick={() => this.onChangeServingsNeeded(Math.max(1, requestedServings - 1))}
                                        disabled={requestedServings <= 1}
                                    >
                                        -
                                    </button>
                                    <span className="count-display">{requestedServings}</span>
                                    <button
                                        className="count-btn plus"
                                        onClick={() => this.onChangeServingsNeeded(requestedServings + 1)}
                                    >
                                        +
                                    </button>
                                </div>
                                <div className="serving-options">
                                    {servingOptions.map((option, index) => (
                                        <button
                                            key={index}
                                            className={`serving-option ${requestedServings === option.value ? 'active' : ''}`}
                                            onClick={() => this.onChangeServingsNeeded(option.value)}
                                        >
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <footer>
                        <button className="el-modal-cancel-btn" onClick={closeModal}>Cancel</button>
                        <button className="el-modal-ok-btn" onClick={this.onChangeScaling}>Save</button>
                    </footer>
                </div>

                {this.state.showWarningsModal && (
                    <WarningsModal
                        isOpen={this.state.showWarningsModal}
                        closeModal={this.closeWarningsModal}
                        dishes={dishes}
                        contents={contents}
                        participants={[syntheticParticipant]}
                        extraServings={0}
                        plan={null}
                    />
                )}
            </Modal>
        );
    }
}